import { WSS_URL } from "@/config/index";
import i18n from "@/shared/lib/i18n/config";
import { pairsStore } from "@/shared/stores/PairsStore";
import { tickerStore } from "@/shared/stores/TickerStore";
import { userStore } from "@/shared/stores/UserStore";
import { bonusStore } from "@/shared/stores/BonusStore";
import { notificationsStore } from "@/shared/stores/NotificationsStore";
import { addToast } from "@cronika/ui";
import { fromJson } from "@bufbuild/protobuf";
import { BetMessageSchema } from "@cronika/proto/common/trading_pb";
import { comparer, reaction } from "mobx";
import { observer } from "mobx-react-lite";
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { BarUpdateEvent, BetSocketEvent, PaymentEvent } from "./types";

/** Forming M1 candle computed server-side (CandlesAggregator) — the exact
 *  candle that will be persisted when the minute closes. */
export interface FormingM1 {
  ts: number; // period start, seconds
  o: string;
  h: string;
  l: string;
  c: string;
}

interface QuoteData {
  type: "quote";
  ticker: string;
  price: number;
  timestamp: number;
  /** Present on ws-node "q" frames that carry the server forming candle. */
  m1?: FormingM1;
}

interface SimpleSocketContextType {
  isConnected: boolean;
  lastQuote: QuoteData | null;
  lastBet: BetSocketEvent | null;
  lastBar: BarUpdateEvent | null;
  lastPayment: PaymentEvent | null;
}

const SimpleSocketContext = createContext<SimpleSocketContextType | null>(null);

interface SimpleSocketProviderProps {
  children: React.ReactNode;
}

export const SimpleSocketProvider: React.FC<SimpleSocketProviderProps> = observer(({ children }) => {
  const activePair = pairsStore.activePair;

  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [lastQuote, setLastQuote] = useState<QuoteData | null>(null);
  const [lastBet, setLastBet] = useState<BetSocketEvent | null>(null);
  const [lastBar, setLastBar] = useState<BarUpdateEvent | null>(null);
  const [lastPayment, setLastPayment] = useState<PaymentEvent | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const pingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const tickerToSymbolRef = useRef<Map<string, string>>(new Map());
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const shouldReconnectRef = useRef<boolean>(true);

  const formatTicker = (symbol: string): string => {
    return symbol.replace(/\//g, "").toUpperCase();
  };

  const connectWebSocket = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      console.log("WebSocket already connected");
      return;
    }

    console.log("🔌 Connecting WebSocket...");
    // ?app=client tells StockWebsocketHandler which cookie to prefer when both
    // admin_access_token and client_access_token are present (staff who run
    // both apps in one browser).
    const wss = new WebSocket(`${WSS_URL}?app=client`);
    wsRef.current = wss;

    wss.onopen = () => {
      setIsConnected(true);
      if (userStore.authorized) {
        void notificationsStore.loadNotifications();
      }

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      const pairs: string[] = [];
      for (const symbol of tickerStore.subscriptions.keys()) {
        const ticker = formatTicker(symbol);
        tickerToSymbolRef.current.set(ticker, symbol);
        pairs.push(ticker);
      }
      if (pairs.length > 0) {
        wss.send(JSON.stringify({ type: "sub", pairs }));
      }

      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }

      pingIntervalRef.current = setInterval(() => {
        if (wss.readyState === WebSocket.OPEN) {
          wss.send(JSON.stringify({ type: "ping" }));
        }
      }, 30000);
    };

    wss.onclose = () => {
      setIsConnected(false);

      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }

      if (shouldReconnectRef.current) {
        reconnectTimeoutRef.current = setTimeout(() => {
          connectWebSocket();
        }, 2000);
      }
    };

    wss.onerror = (error) => {
      console.error("WebSocket error:", error);
    };

    wss.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        if (data.type === "q") {
          // ws-node tick frame:
          //   { type: "q", data: { "EURUSD": "1.12345" }, c1m: { "EURUSD": {ts,o,h,l,c} } }
          // `c1m` (optional) is the server-side forming M1 candle — passed through
          // so the chart renders the exact candle the backend will persist,
          // instead of rebuilding candles from raw quotes and diverging.
          const entries = Object.entries(data.data as Record<string, string>);
          const c1m = (data.c1m ?? {}) as Record<string, FormingM1>;
          for (const [ticker, priceStr] of entries) {
            const price = parseFloat(priceStr);
            setLastQuote({ type: "quote", ticker, price, timestamp: Date.now(), m1: c1m[ticker] });
            const appSymbol =
              tickerToSymbolRef.current.get(ticker) ??
              Array.from(tickerToSymbolRef.current.entries()).find(([k]) => k === ticker)?.[1];
            if (appSymbol) tickerStore.updatePrice(appSymbol, price);
          }
        } else if (data.type === "c") {
          // ws-node candle close frame — chart builds itself via useDataUpdateLoop
          // from raw quotes + wall clock; calling setLastBar here would double-
          // increment timeRef (useDataUpdateLoop already advanced it) causing a
          // ghost empty candle. We intentionally ignore this for chart rendering.
          // (nothing to do)
        } else if (data.type === "hello" || data.type === "pong") {
          // ignore keepalive frames
        } else if (data.type === "quote") {
          // Legacy format from old WS server (devtick.cronika.com)
          setLastQuote(data as QuoteData);
        } else if (data.type === "bar" || (data.symbol && data.bar)) {
          // Legacy format from old WS server
          const barData = data as BarUpdateEvent;
          const barClose = barData.bar?.close;
          const barSym = barData.symbol;
          if (barSym && barClose) {
            const appSymbol =
              tickerToSymbolRef.current.get(barSym) ??
              tickerToSymbolRef.current.get(barSym.replace(/\//g, ""));
            if (appSymbol) tickerStore.updatePrice(appSymbol, barClose);
          }
          setLastBar(barData);
        } else if (data.type && typeof data.type === "string" && data.type.startsWith("bet.")) {
          try {
            const betMessage = fromJson(BetMessageSchema, data.data);
            setLastBet({ type: data.type, data: betMessage } as BetSocketEvent);
          } catch (e) {
            console.error("Failed to parse BetMessage:", e);
          }
        } else if (data.type === "payment.status") {
          const paymentData = data.data as PaymentEvent;
          setLastPayment(paymentData);
          const amount = parseFloat(paymentData.amount).toFixed(2);
          switch (paymentData.status) {
            case "completed":
              addToast({
                title: i18n.t("deposit:toast.success-completed.title"),
                description: i18n.t("deposit:toast.success-completed.description", { amount }),
                color: "success",
                timeout: 5000,
              });
              // Backend has persisted the deposit notification by now — reload
              // the list so the bell reflects it (and the related deposit data).
              void notificationsStore.loadNotifications();
              userStore.refreshBalance();
              break;
            case "failed":
              addToast({
                title: i18n.t("deposit:toast.failed.title"),
                description: i18n.t("deposit:toast.failed.description", { amount }),
                color: "danger",
                timeout: 5000,
              });
              break;
            default:
              break;
          }
        } else if (data.type === "bar" || (data.symbol && data.bar)) {
          setLastBar(data as BarUpdateEvent);
          // Update tickerStore with the bar close price so ActiveBetCard predict
          // uses the same price the chart displays (candle close, not raw quote).
          // Try both the formatted ticker key and the raw symbol in case the server
          // sends bar.symbol with or without slashes.
          const barData = data as BarUpdateEvent;
          const barClose = barData.bar?.close;
          const barSym = barData.symbol;
          if (barSym && barClose) {
            const appSymbol =
              tickerToSymbolRef.current.get(barSym) ??
              tickerToSymbolRef.current.get(barSym.replace(/\//g, ""));
            if (appSymbol) tickerStore.updatePrice(appSymbol, barClose);
          }
        } else if (data.type === "user.force_logout") {
          userStore.logout();
        } else if (data.type === "user.profile_updated") {
          // KYC and other admin-side profile changes must affect every open tab
          // immediately; the periodic auth refresh remains a fallback.
          void userStore.refreshProfile().catch((error) => {
            console.error("Failed to refresh profile after socket update:", error);
          });
        } else if (data.type === "bonus.cancelled") {
          bonusStore.reset();
          void bonusStore.fetchCurrent();
          userStore.refreshBalance();
        } else if (data.type !== undefined) {
          // Silently ignore server-side keepalive/ping messages (e.g. type:0)
          // and any other numeric or unrecognised types — logging them spams
          // the console at the rate the server sends heartbeats.
        }
      } catch (error) {
        console.error("Error parsing message:", error);
      }
    };
  }, []);

  useEffect(() => {
    shouldReconnectRef.current = true;
    connectWebSocket();

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        const wss = wsRef.current;
        if (userStore.authorized) {
          void notificationsStore.loadNotifications();
        }

        if (!wss || wss.readyState === WebSocket.CLOSED || wss.readyState === WebSocket.CLOSING) {
          connectWebSocket();
        }
      }
    };

    const handleOnline = () => {
      if (userStore.authorized) {
        void notificationsStore.loadNotifications();
      }
      connectWebSocket();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("online", handleOnline);

    return () => {
      shouldReconnectRef.current = false;

      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("online", handleOnline);

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }

      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }

      const wss = wsRef.current;
      if (wss && wss.readyState === WebSocket.OPEN) {
        wss.close();
      }
    };
  }, [connectWebSocket]);

  useEffect(() => {
    if (!activePair?.symbol) return;
    const symbol = activePair.symbol;
    tickerStore.subscribe(symbol);
    return () => tickerStore.unsubscribe(symbol);
  }, [activePair?.symbol]);

  useEffect(() => {
    setLastQuote(null);
  }, [activePair]);

  useEffect(() => {
    const dispose = reaction(
      () => Array.from(tickerStore.subscriptions.keys()).sort(),
      (current, previous) => {
        const wss = wsRef.current;
        if (!wss || wss.readyState !== WebSocket.OPEN) return;

        const prevSet = new Set(previous);
        const currSet = new Set(current);

        const toSub: string[] = [];
        for (const symbol of current) {
          if (!prevSet.has(symbol)) {
            const ticker = formatTicker(symbol);
            tickerToSymbolRef.current.set(ticker, symbol);
            toSub.push(ticker);
          }
        }
        if (toSub.length > 0) wss.send(JSON.stringify({ type: "sub", pairs: toSub }));

        const toUnsub: string[] = [];
        for (const symbol of previous) {
          if (!currSet.has(symbol)) {
            const ticker = formatTicker(symbol);
            tickerToSymbolRef.current.delete(ticker);
            toUnsub.push(ticker);
          }
        }
        if (toUnsub.length > 0) wss.send(JSON.stringify({ type: "unsub", pairs: toUnsub }));
      },
      { equals: comparer.structural, fireImmediately: false },
    );
    return dispose;
  }, []);

  const contextValue: SimpleSocketContextType = {
    isConnected,
    lastQuote,
    lastBet,
    lastBar,
    lastPayment,
  };

  return <SimpleSocketContext.Provider value={contextValue}>{children}</SimpleSocketContext.Provider>;
});

export const useSimpleSocket = (): SimpleSocketContextType => {
  const context = useContext(SimpleSocketContext);
  if (!context) {
    throw new Error("useSimpleSocket must be used within a SimpleSocketProvider");
  }
  return context;
};

export const useSimpleSocketOptional = (): SimpleSocketContextType | null => {
  return useContext(SimpleSocketContext);
};

export const useQuoteUpdates = () => {
  const { lastQuote } = useSimpleSocket();

  return { lastQuote };
};

export const useBetUpdates = () => {
  const { lastBet } = useSimpleSocket();
  return {
    lastBet,
    data: lastBet ? [lastBet] : [],
    isSubscribed: true,
  };
};

export const useBarUpdates = () => {
  const { lastBar } = useSimpleSocket();
  return {
    lastBar,
    bars: lastBar ? [lastBar] : [],
    isSubscribed: true,
  };
};

export const usePaymentUpdates = () => {
  const { lastPayment } = useSimpleSocket();
  return { lastPayment };
};
